import {
  AddIcon,
  Box,
  Button,
  CheckIcon,
  Flex,
  Image,
  Link,
  Typography as ReeferTypography,
  StarFilledIcon,
} from '@iheartjane/reefer';
import { sentenceCase } from '@iheartjane/utils';
import { clsx } from 'clsx';
import type { ComponentType, MouseEvent } from 'react';
import { useEffect, useState } from 'react';

import {
  LINEAGE_COLORS,
  REVIEWS_REQUIRED_BEFORE_DISPLAY,
} from '../../../utils/constants';
import type {
  AdPriceColors,
  CustomImageAdProduct,
  CustomImageAdViewModel,
} from '../webCustomImageAd.types';

import styles from './adProductCard.module.css';

const ADDED_FEEDBACK_MS = 250;

const DEFAULT_PRICE_COLORS: AdPriceColors = {
  deal: 'info',
  regular: 'grays-black',
  strikethrough: 'grays-mid',
};

interface AdProductCardProps {
  onAddToCart?: (product: CustomImageAdViewModel) => void;
  onImageLoad?: () => void;
  onProductClick?: (product: CustomImageAdProduct) => void;
  priceColors?: AdPriceColors;
  product: CustomImageAdViewModel;
  showPrice?: boolean;
  typography?: ComponentType;
}

export const AdProductCard = ({
  onAddToCart,
  onImageLoad,
  onProductClick,
  priceColors = DEFAULT_PRICE_COLORS,
  product,
  showPrice = false,
  typography,
}: AdProductCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showAdded, setShowAdded] = useState(false);
  const Typography = typography || ReeferTypography;

  const showRating =
    product.aggregate_rating > 0 &&
    product.review_count >= REVIEWS_REQUIRED_BEFORE_DISPLAY;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    product.clickHandler();
    onProductClick?.(product);
    event.stopPropagation();
  };

  const handleAddToCart = (event: MouseEvent) => {
    event.stopPropagation();
    product.clickHandler();
    onAddToCart?.(product);
    setShowAdded(true);
  };

  useEffect(() => {
    if (!showAdded) return;

    const timeout = setTimeout(() => setShowAdded(false), ADDED_FEEDBACK_MS);
    return () => clearTimeout(timeout);
  }, [showAdded]);

  const lineageColor = product.category
    ? LINEAGE_COLORS[product.category.toLowerCase()] || 'grays-black'
    : 'grays-black';

  const rating = (
    <Flex alignItems="center" gap={4}>
      <StarFilledIcon size="sm" />
      <Typography variant="small-body-semibold" color="grays-dark" maxLines={1}>
        {product.aggregate_rating}
        <span className={styles['reviewCount']}>({product.review_count})</span>
      </Typography>
    </Flex>
  );

  const AddToCartIcon = showAdded ? CheckIcon : AddIcon;

  return (
    <Flex
      className={clsx(styles['card'], {
        [styles['imageNotLoaded']]: !imageLoaded,
      })}
      background="grays-white"
      flexDirection="column"
      borderRadius="xs"
      gap={12}
      height="100%"
      overflow="hidden"
      p={12}
      position="relative"
    >
      <Box
        className={styles['imageSlot']}
        borderRadius="sm"
        overflow="hidden"
        position="relative"
        width="100%"
      >
        <Image
          altText={product.name}
          src={product.image_urls[0]}
          objectFit="cover"
          responsive
          width="100%"
          height="100%"
          loading="lazy"
          onLoad={() => {
            setImageLoaded(true);
            onImageLoad?.();
          }}
        />

        {onAddToCart && (
          <Button.Icon
            className={styles['addToCart']}
            variant="minimal-inverse"
            size="small"
            icon={<AddToCartIcon className={styles['addToCartIcon']} />}
            label={`Add ${product.name} to bag`}
            onClick={handleAddToCart}
          />
        )}
      </Box>

      <Link
        className={styles['link']}
        to={product.pdpUrl}
        variant="minimal"
        onClick={handleClick}
      >
        <Flex
          className={clsx({ [styles['besideAddToCart']]: onAddToCart })}
          flexDirection="column"
        >
          <Flex
            alignItems="center"
            gap={4}
            width="100%"
            justifyContent="space-between"
          >
            <Typography
              className={styles['lineage']}
              variant="small-body-regular"
              color={lineageColor}
              maxLines={1}
            >
              {product.category ? sentenceCase(product.category) : ''}
            </Typography>

            {showRating && !showPrice && rating}
          </Flex>

          <Typography
            className={clsx(styles['name'], {
              [styles['twoLineName']]: !showPrice,
            })}
            variant="small-body-semibold"
            color="grays-black"
            maxLines={showPrice ? 1 : 2}
            overflowWrap="anywhere"
          >
            {product.name}
          </Typography>

          {showRating && showPrice && rating}

          {showPrice && product.price && (
            <Flex alignItems="center" gap={4}>
              <Typography
                variant="small-body-semibold"
                color={
                  product.isDiscounted ? priceColors.deal : priceColors.regular
                }
                maxLines={1}
              >
                {product.price}
              </Typography>
              {product.originalPrice && (
                <Typography
                  variant="small-body-regular"
                  color={priceColors.strikethrough}
                  strikeThrough
                >
                  {product.originalPrice}
                </Typography>
              )}
            </Flex>
          )}
        </Flex>
      </Link>
    </Flex>
  );
};
